import React, { useEffect, useState } from 'react'
import { AppShell, Footer, Navbar } from '@mantine/core'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'
import Sidebar from '../components/Sidebar'
import MainNav from '../components/MainNav'
import BillingAlertFooter from '../components/BillingAlertFooter'
import OrgAdminStatusBar from '../components/OrgAdminStatusBar'
import AppInitializer from '../components/AppInitializer'
import ExperienceInitializer from '../components/ExperienceInitializer'
import { useDisclosure, useResizeObserver } from '@mantine/hooks'
import { useDispatch, useSelector } from 'react-redux'
import { switchOrg, switchExperience } from '../services/actions/userActions'
import SwitchOrgModal from '../components/SwitchOrgModal'
import SwitchExperienceModal from '../components/SwitchExperienceModal'
import { loadUserContext } from '../services/utils/loadUserContext'
import { setCurrentExperience } from '../services/store/userSlice'
import DeactivatedOrgPage from '../pages/DeactivatedOrgPage'

const PrivateLayout = ({ colorScheme, toggleColorScheme }) => {
	const { orgSlug } = useParams()
	const location = useLocation()
	const navigate = useNavigate()
	const dispatch = useDispatch()
	const [opened, { toggle, close }] = useDisclosure(false)
	const [footerRef, footerRect] = useResizeObserver()

	const currentOrg = useSelector((state) => state.user.currentOrg)
	const [showOrgModal, setShowOrgModal] = useState(false)
	const [showExpModal, setShowExpModal] = useState(false)

	useEffect(() => {
		close()
	}, [location.pathname, close])

	const handleSwitchOrg = async (org) => {
		try {
			await switchOrg(org.orgSlug)
			dispatch(setCurrentExperience(null))
			await loadUserContext(dispatch)
			setShowOrgModal(false)
			navigate(`/`)
		} catch (err) {
			console.error('Failed to switch organization:', err)
		}
	}

	const handleSwitchExperience = async (exp) => {
		try {
			await switchExperience(exp.experienceSlug)
			await loadUserContext(dispatch)
			navigate(`/${orgSlug}/experiences/${exp.experienceSlug}`)
			setShowExpModal(false)
		} catch (err) {
			console.error('Failed to switch experience', err)
		}
	}

	return (
		<>
			<AppInitializer />
			<ExperienceInitializer />

			<AppShell
				navbarOffsetBreakpoint='sm'
				navbar={
					<Navbar
						p='md'
						hiddenBreakpoint='sm'
						hidden={!opened}
						width={{ sm: 250, lg: 300 }}
					>
						<Sidebar />
					</Navbar>
				}
				header={
					<MainNav
						opened={opened}
						toggle={toggle}
						setShowOrgModal={setShowOrgModal}
						setShowExpModal={setShowExpModal}
						colorScheme={colorScheme}
						toggleColorScheme={toggleColorScheme}
					/>
				}
				padding='md'
				styles={(theme) => ({
					main: {
						backgroundColor: theme.colorScheme === 'dark'
							? theme.colors.dark[8]
							: theme.white,
					},
				})}
				footer={
					<Footer
						height={Math.ceil(footerRect.height)}
						p={0}
						zIndex={100}
						styles={{ root: { borderTop: 0 } }}
					>
						<div ref={footerRef}>
							<BillingAlertFooter embedded />
							<OrgAdminStatusBar />
						</div>
					</Footer>
				}
			>
				<div key={currentOrg?.orgSlug || 'no-org'}>
					{currentOrg?.isActive === false ? <DeactivatedOrgPage /> : <Outlet />}
				</div>
			</AppShell>

			<SwitchOrgModal
				show={showOrgModal}
				onClose={() => setShowOrgModal(false)}
				onSelect={handleSwitchOrg}
			/>
			<SwitchExperienceModal
				show={showExpModal}
				onClose={() => setShowExpModal(false)}
				onSelect={handleSwitchExperience}
			/>
		</>
	)
}

export default PrivateLayout
